<script setup lang="ts">
import { PoolDetails } from "@/models/pool-details.model";
import { computed, ComputedRef, inject } from "vue";
import { isNotNil } from "@/models/nullable.type";
import {
  getOutByIndex,
  getOutByName,
  shouldHideOut,
} from "@/shared/utils/outs";
import KlereoIcon from "@/shared/components/KlereoIcon.vue";
import PoolCoverCard from "@/features/pool/PoolCoverCard.vue";
import ProbeCard from "@/features/pool/dashboard/ProbeCard.vue";
import OutCard from "@/features/pool/dashboard/OutCard.vue";
import { getProbeType } from "@/shared/utils/probes";
import ParamsButton from "@/features/pool/dashboard/ParamsButton.vue";
import { Token } from "@/models/auth.models.ts";
import { Pool } from "@/models/pool.model.ts";

const COUVERTURE_IDX = 8;

const dashboardProbes = [
  { idxField: "EauCapteur" },
  { idx: 1 },
  { idxField: "PressionCapteur" },
  { idxField: "pHCapteur" },
  { idxField: "TraitCapteur" },
] as { idx?: number; idxField?: keyof PoolDetails }[];

const pool = inject("pool") as ComputedRef<Pool>;
const token = inject("token") as ComputedRef<Token>;

const probes = computed(() =>
  dashboardProbes
    .map(({ idx, idxField }) => {
      const probeIdx = (idxField ? pool.value[idxField] : idx) as number;
      return pool.value.probes.find((probe) => probe.index === probeIdx);
    })
    .filter(isNotNil)
    .map((probe) => ({
      probe,
      out: getProbeType(probe.type)?.out
        ? pool.value.outs.find(
            (o) =>
              o.index ===
              getOutByName(getProbeType(probe.type).out as string).index
          )
        : undefined,
    }))
);

const otherProbes = computed(() =>
  pool.value.probes.filter(
    (probe) =>
      probe.index !== COUVERTURE_IDX &&
      !probes.value.some((p) => p.probe.index === probe.index)
  )
);

const extraProbes = computed(() =>
  otherProbes.value
    .filter((p) => p && !getProbeType(p.type)?.name.includes("BIDON"))
    .map((probe) => ({ probe, out: undefined }))
);

const bidonProbes = computed(() =>
  otherProbes.value.filter(
    (p) => p && getProbeType(p.type)?.name.includes("BIDON")
  )
);

const firstProbes = computed(() => [...probes.value, ...extraProbes.value]);

const outs = computed(() =>
  pool.value.outs.filter(
    (out) =>
      getOutByIndex(out.index).name === "OUT_CHAUF" ||
      (!shouldHideOut(out, pool.value) &&
        !probes.value.some((probe) => probe.out?.index === out.index))
  )
);

const couverture = computed(() =>
  pool.value.probes.find((p) => p.index === COUVERTURE_IDX)
);
</script>

<template>
  <div class="lg:flex lg:gap-10 lg:max-w-3xl lg:mx-auto lg:items-start">
    <div
      class="grid grid-cols-2 gap-1.5 flex-1"
      v-if="firstProbes.length + bidonProbes.length > 0"
    >
      <div
        class="hidden lg:block text-bluenew text-lg font-medium text-center col-span-2 my-3"
        v-if="firstProbes.length > 0"
      >
        {{ $t("DashboardProbes") }}
      </div>
      <probe-card
        v-for="probe in firstProbes"
        :key="probe.probe.index"
        :out="probe.out"
        :probe="probe.probe"
        :span="
          probe.probe.index === pool.EauCapteur && firstProbes.length % 2 === 1
            ? 2
            : 1
        "
      />

      <div
        class="hidden lg:block text-bluenew text-lg font-medium text-center col-span-2 my-3"
        v-if="bidonProbes.length > 0"
      >
        {{ $t("OtherCap") }}
      </div>

      <probe-card
        class="hidden lg:block"
        v-for="probe in bidonProbes"
        :key="probe.index"
        :probe="probe"
        :span="1"
      />
    </div>

    <div
      class="grid grid-cols-2 gap-1.5 flex-1"
      v-if="
        outs.length > 0 ||
        pool.alerts?.length > 0 ||
        (pool.access ?? token.dac) >= 25
      "
    >
      <div
        class="text-[#F8F8F8] font-medium my-2.5 col-span-2 lg:my-3 lg:text-bluenew lg:text-lg lg:text-center"
        v-if="outs.length > 0"
      >
        {{ $t("DashboardOuts") }}
      </div>

      <div v-if="couverture" class="card col-span-2 p-4 flex items-center">
        <pool-cover-card :value="couverture.directValue" />
      </div>

      <out-card
        v-for="out in outs"
        :out="out"
        :key="out.index"
        class="card text-center p-4 font-medium cursor-pointer flex flex-col items-center"
      />

      <div
        class="lg:hidden text-[#F8F8F8] font-medium my-2.5 col-span-2 lg:my-3 lg:text-bluenew lg:text-lg lg:text-center"
        v-if="bidonProbes.length > 0"
      >
        {{ $t("OtherCap") }}
      </div>

      <probe-card
        class="lg:hidden"
        v-for="probe in otherProbes"
        :key="probe.index"
        :probe="probe"
        :span="1"
      />

      <div
        class="text-[#F8F8F8] font-medium my-2.5 col-span-2 lg:my-3 lg:text-bluenew lg:text-lg lg:text-center"
        v-if="pool.alerts?.length > 0 || (pool.access ?? token.dac) >= 25"
      >
        {{ $t("DashboardInfos") }}
      </div>

      <router-link
        class="card text-center p-4 font-medium cursor-pointer flex flex-col items-center justify-center"
        :to="`./${$route.params.poolId}/alerts`"
        v-if="pool.alerts?.length > 0"
        ><span class="text-bluenew uppercase">
          {{ $t("Alert") }} ({{ pool.alerts?.length ?? 0 }})
        </span>
        <klereo-icon name="bell" class="m-2 w-[32px]" />
      </router-link>

      <ParamsButton />
    </div>
  </div>
</template>

<style>
.measure-name {
  @apply text-bluenew;
  @apply text-lg;
  @apply font-medium;
  overflow: hidden;
  text-overflow: ellipsis;
}

.measure-value {
  @apply text-4xl;
  @apply overflow-hidden;
  @apply py-5;
  text-overflow: ellipsis;
}
</style>
